import { cn } from '@atlas/ui-primitives'
import { isPlaceholderToken, type PromptVariable, placeholderSplitRegex } from '@/domain/prompt.types'

export interface PromptBodyPreviewProps {
	body: string
	/** Declared variables — a placeholder not among them is highlighted as undeclared. */
	variables: PromptVariable[]
	className?: string
}

/** The key inside a matched placeholder, without the braces or padding. */
const keyOf = (token: string) => token.replace(/[{}]/g, '').trim()

/**
 * The read-only prompt body with its `{{placeholders}}` highlighted.
 *
 * Highlighting lives on the READ view rather than in the editor: a `<textarea>` cannot
 * style its own content, and the overlay trick that fakes it (a positioned mirror
 * behind a transparent input) desynchronises on scroll, wrapping and IME input. The
 * read view is also where highlighting earns the most — it is what a reviewer scans to
 * see which values a prompt expects.
 *
 * An UNDECLARED placeholder is called out distinctly: it is the one thing here that
 * will actually fail at render time, and the lint message below the editor names it
 * but does not show where it sits in the body.
 *
 * Presentational + storied.
 */
export function PromptBodyPreview({ body, variables, className }: PromptBodyPreviewProps) {
	const declared = new Set(variables.map((v) => v.key))

	return (
		<pre
			className={cn(
				'whitespace-pre-wrap rounded-ds-md border border-ds-border bg-ds-surface p-3 font-mono text-ds-sm text-ds-text',
				className,
			)}
		>
			{/* split() with a capturing group keeps the delimiters, so the body is
			    reassembled exactly — no character is dropped or re-ordered. */}
			{body.split(placeholderSplitRegex()).map((part, index) => {
				if (!isPlaceholderToken(part)) return <span key={`t-${index}`}>{part}</span>

				// A dotted path (`item.field`) belongs to an `each` item template, so its root
				// is what has to be declared.
				const root = keyOf(part).split('.')[0] ?? ''
				const isDeclared = declared.has(root)

				return (
					<span
						key={`p-${index}`}
						className={cn(
							'rounded-ds-sm px-0.5',
							isDeclared ? 'bg-ds-primary/15 text-ds-text' : 'bg-ds-danger/15 text-ds-danger',
						)}
					>
						{/* Named for assistive tech, since the colour is the only visual signal. */}
						{isDeclared ? null : <span className="sr-only">undeclared variable: </span>}
						{part}
					</span>
				)
			})}
		</pre>
	)
}
